import React, { useState, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import { Canvas, useFrame, useLoader } from "@react-three/fiber";
import { OrbitControls, useGLTF } from "@react-three/drei";
import { motion, AnimatePresence } from "framer-motion";
import ReactMarkdown from "react-markdown";
import Spline from "@splinetool/react-spline";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import * as THREE from "three";

import CylindricalCarousel from "./components/CylindricalCarousel";
import ImageCarousel from "./components/ImageCarousel";
import Scene from "./components/CylinderWithCarousel"; // <-- the above file
import Banner from "./components/Banner";
import CategoriesSection from "./components/CategoriesSection";
import Impressum from "./pages/Impressum";
import ContactUs from "./pages/ContactUs";
import AboutUs from "./pages/AboutUs";
import Footer from "./components/Footer";
import Navbar from "./components/Navbar";
import Haftungsausschluss from "./pages/Haftungsausschluss";
import Login from "./components/Login";

// --------------------------------------------------------------------------
// Cookie Consent Popup Component
// --------------------------------------------------------------------------
function CookieConsentPopup() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem("cookieConsent");
    if (!consent) {
      setIsVisible(true);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem("cookieConsent", "true");
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-gray-800 text-white p-4 z-50">
      <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between">
        <p className="mb-2 sm:mb-0 text-sm">
          Diese Website verwendet Cookies, um die Nutzererfahrung zu verbessern,
          Inhalte und Anzeigen zu personalisieren, Funktionen für soziale Medien
          anbieten zu können und den Datenverkehr zu analysieren. Durch die
          weitere Nutzung der Website stimmen Sie der Verwendung von Cookies zu.
          Weitere Informationen finden Sie in unserer{" "}
          <Link to="/datenschutz" className="underline ml-1">
            Datenschutzerklärung
          </Link>.
        </p>
        <button
          onClick={handleAccept}
          className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded"
        >
          Akzeptieren
        </button>
      </div>
    </div>
  );
}

// Layout component to wrap all pages
function Layout({ children }) {
  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <main className="flex-grow pt-16">{children}</main>
      <Footer />
    </div>
  );
}

// --------------------------------------------------------------------------
// Spline Section with Toggleable Text (Hero Section)
// --------------------------------------------------------------------------
function SplineSection() {
  const [showText, setShowText] = useState(true);

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);
    if (showText) {
      gsap.fromTo(
        ".left-text",
        { opacity: 0, x: -100 },
        {
          opacity: 1,
          x: 0,
          duration: 1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: ".left-text",
            start: "top 90%",
            scroller: ".scroll-container",
            toggleActions: "play none none reverse",
          },
        }
      );

      gsap.fromTo(
        ".right-text",
        { opacity: 0, x: 100 },
        {
          opacity: 1,
          x: 0,
          duration: 1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: ".right-text",
            start: "top 90%",
            scroller: ".scroll-container",
            toggleActions: "play none none reverse",
          },
        }
      );
    }
  }, [showText]);

  return (
    <section id="spline" className="w-full h-screen relative">
      <Spline
        scene="https://prod.spline.design/5hR106eKNkzaYaCa/scene.splinecode"
        style={{ pointerEvents: "none" }}
      />

      {showText && (
        <>
          <div className="left-text absolute top-1/2 left-4 transform -translate-y-1/2 max-w-md text-white font-montserrat drop-shadow-lg">
            <h2 className="font-bold text-3xl mb-4 text-[#47B9BB]">
              Ihr Partner für Präzision und Innovation
            </h2>
            <p className="text-lg leading-relaxed">
              Seit über einem Jahrhundert steht Heidenau für Qualität und
              Expertise in der Fertigung von Ausrüstungen. Unser Know-how wurde
              über Generationen kontinuierlich weiterentwickelt.
            </p>
            <p className="text-lg mt-4 leading-relaxed">
              Mit der Gründung im Jahr 2004 verbinden wir Tradition mit
              modernster Technik – und setzen bewährte Techniken fort.
            </p>
          </div>

          <div className="right-text absolute top-1/2 right-4 transform -translate-y-1/2 max-w-md text-white text-right font-montserrat drop-shadow-lg">
            <h2 className="font-bold text-3xl mb-4 text-[#47B9BB]">
              Innovation trifft Tradition
            </h2>
            <p className="text-lg leading-relaxed">
              Unser erfahrenes Team arbeitet stetig daran, innovative Lösungen
              zu entwickeln – immer mit Blick auf höchste Präzision und
              Qualität.
            </p>
            <p className="text-lg mt-4 leading-relaxed">
              Vertrauen Sie auf jahrzehntelange Expertise und modernste
              Technologie für Ihre Projekte.
            </p>
          </div>
        </>
      )}

      <button
        onClick={() => setShowText((prev) => !prev)}
        className="absolute bottom-4 left-1/2 transform -translate-x-1/2 p-2 text-white bg-gray-800 bg-opacity-80 rounded-full shadow-lg"
      >
        {showText ? "▲" : "▼"}
      </button>
    </section>
  );
}

// --------------------------------------------------------------------------
// Privacy Policy Page Component
// --------------------------------------------------------------------------
function PrivacyPolicyPage() {
  return (
    <div className="relative font-sans bg-[#060606] text-gray-100 min-h-screen">
      <nav className="fixed top-0 left-0 right-0 z-30 bg-[#060606] bg-opacity-90 shadow">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="text-white font-bold">
            Zurück
          </Link>
          <h1 className="text-xl font-bold text-blue-400">Datenschutzerklärung</h1>
          <div></div>
        </div>
      </nav>
      <div className="pt-20 p-6 max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-6">Datenschutzerklärung</h1>
        <section className="mb-8">
          <h2 className="text-2xl font-bold mb-4">
            1. Datenschutz auf einen Blick
          </h2>
          <h3 className="text-xl font-semibold mb-2">Allgemeine Hinweise</h3>
          <p className="mb-4">
            Die folgenden Hinweise geben einen einfachen Überblick darüber, was
            mit Ihren personenbezogenen Daten passiert, wenn Sie unsere Website
            besuchen. Personenbezogene Daten sind alle Daten, mit denen Sie
            persönlich identifiziert werden können. Ausführliche Informationen
            zum Thema Datenschutz entnehmen Sie unserer unter diesem Text
            aufgeführten Datenschutzerklärung.
          </p>
          <h3 className="text-xl font-semibold mb-2">
            Datenerfassung auf unserer Website
          </h3>
          <h4 className="font-medium">
            Wer ist verantwortlich für die Datenerfassung auf dieser Website?
          </h4>
          <p className="mb-4">
            Die Datenverarbeitung auf dieser Website erfolgt durch den
            Websitebetreiber. Dessen Kontaktdaten können Sie dem Impressum
            dieser Website entnehmen.
          </p>
          <h4 className="font-medium">Wie erfassen wir Ihre Daten?</h4>
          <p className="mb-4">
            Ihre Daten werden zum einen dadurch erhoben, dass Sie uns diese
            mitteilen. Hierbei kann es sich z.B. um Daten handeln, die Sie in
            ein Kontaktformular eingeben.
          </p>
          <p className="mb-4">
            Andere Daten werden automatisch beim Besuch der Website durch
            unsere IT-Systeme erfasst. Das sind vor allem technische Daten (z.B.
            Internetbrowser, Betriebssystem oder Uhrzeit des Seitenaufrufs). Die
            Erfassung dieser Daten erfolgt automatisch, sobald Sie unsere
            Website betreten.
          </p>
          <h4 className="font-medium">Wofür nutzen wir Ihre Daten?</h4>
          <p className="mb-4">
            Ein Teil der Daten wird erhoben, um eine fehlerfreie Bereitstellung
            der Website zu gewährleisten. Andere Daten können zur Analyse Ihres
            Nutzerverhaltens verwendet werden.
          </p>
          <h4 className="font-medium">
            Welche Rechte haben Sie bezüglich Ihrer Daten?
          </h4>
          <p>
            Sie haben jederzeit das Recht unentgeltlich Auskunft über Herkunft,
            Empfänger und Zweck Ihrer gespeicherten personenbezogenen Daten zu
            erhalten. Sie haben außerdem ein Recht, die Berichtigung, Sperrung
            oder Löschung dieser Daten zu verlangen. Hierzu sowie zu weiteren
            Fragen zum Thema Datenschutz können Sie sich jederzeit unter der im
            Impressum angegebenen Adresse an uns wenden. Des Weiteren steht
            Ihnen ein Beschwerderecht bei der zuständigen Aufsichtsbehörde zu.
          </p>
          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">
              2. Allgemeine Hinweise und Pflichtinformationen
            </h2>
            <h3 className="text-xl font-semibold mb-2">Datenschutz</h3>
            <p className="mb-4">
              Die Betreiber dieser Seiten nehmen den Schutz Ihrer persönlichen
              Daten sehr ernst. Wir behandeln Ihre personenbezogenen Daten
              vertraulich und entsprechend der gesetzlichen
              Datenschutzvorschriften sowie dieser Datenschutzerklärung.
            </p>
            <p className="mb-4">
              Wenn Sie diese Website benutzen, werden verschiedene
              personenbezogene Daten erhoben. Personenbezogene Daten sind Daten,
              mit denen Sie persönlich identifiziert werden können. Die
              vorliegende Datenschutzerklärung erläutert, welche Daten wir
              erheben und wofür wir sie nutzen. Sie erläutert auch, wie und zu
              welchem Zweck das geschieht.
            </p>
            <p className="mb-4">
              Wir weisen darauf hin, dass die Datenübertragung im Internet (z.B.
              bei der Kommunikation per E-Mail) Sicherheitslücken aufweisen
              kann. Ein lückenloser Schutz der Daten vor dem Zugriff durch
              Dritte ist nicht möglich.
            </p>
            <h3 className="text-xl font-semibold mb-2">
              Hinweis zur verantwortlichen Stelle
            </h3>
            <p className="mb-4">
              Die verantwortliche Stelle für die Datenverarbeitung auf dieser
              Website ist:
            </p>
            <p className="mb-4">
              G-A-B-S GmbH
              <br />
              Geschäftsführer: Michael Pastrick
              <br />
              Gabelsberger Str. 4
              <br />
              01809 Heidenau
              <br />
              Telefon: 03529-513372
              <br />
              E-Mail:{" "}
              <a href="mailto:info@gabs-sachsen.de" className="underline">
                info@g-a-b-s.de
              </a>
            </p>
            <p className="mb-4">
              Verantwortliche Stelle ist die natürliche oder juristische
              Person, die allein oder gemeinsam mit anderen über die Zwecke und
              Mittel der Verarbeitung von personenbezogenen Daten (z.B. Namen,
              E-Mail-Adressen o. Ä.) entscheidet.
            </p>
            <h3 className="text-xl font-semibold mb-2">
              Widerruf Ihrer Einwilligung zur Datenverarbeitung
            </h3>
            <p className="mb-4">
              Viele Datenverarbeitungsvorgänge sind nur mit Ihrer ausdrücklichen
              Einwilligung möglich. Sie können eine bereits erteilte
              Einwilligung jederzeit widerrufen. Dazu reicht eine formlose
              Mitteilung per E-Mail an uns. Die Rechtmäßigkeit der bis zum
              Widerruf erfolgten Datenverarbeitung bleibt vom Widerruf
              unberührt.
            </p>
            <h3 className="text-xl font-semibold mb-2">
              Beschwerderecht bei der zuständigen Aufsichtsbehörde
            </h3>
            <p className="mb-4">
              Im Falle datenschutzrechtlicher Verstöße steht dem Betroffenen ein
              Beschwerderecht bei der zuständigen Aufsichtsbehörde zu.
              Zuständige Aufsichtsbehörde in datenschutzrechtlichen Fragen ist
              der Landesdatenschutzbeauftragte des Bundeslandes, in dem unser
              Unternehmen seinen Sitz hat. Eine Liste der
              Datenschutzbeauftragten sowie deren Kontaktdaten können folgendem
              Link entnommen werden:{" "}
              <a
                href="https://www.bfdi.bund.de/DE/Infothek/Anschriften_Links/anschriften_links-node.html"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                https://www.bfdi.bund.de/DE/Infothek/Anschriften_Links/anschriften_links-node.html
              </a>
              .
            </p>
            <h3 className="text-xl font-semibold mb-2">
              Recht auf Datenübertragbarkeit
            </h3>
            <p className="mb-4">
              Sie haben das Recht, Daten, die wir auf Grundlage Ihrer
              Einwilligung oder in Erfüllung eines Vertrags automatisiert
              verarbeiten, an sich oder an einen Dritten in einem gängigen,
              maschinenlesbaren Format aushändigen zu lassen. Sofern Sie die
              direkte Übertragung der Daten an einen anderen Verantwortlichen
              verlangen, erfolgt dies nur, soweit es technisch machbar ist.
            </p>
            <h3 className="text-xl font-semibold mb-2">
              SSL- bzw. TLS-Verschlüsselung
            </h3>
            <p className="mb-4">
              Diese Seite nutzt aus Sicherheitsgründen und zum Schutz der
              Übertragung vertraulicher Inhalte, wie zum Beispiel Bestellungen
              oder Anfragen, die Sie an uns als Seitenbetreiber senden, eine
              SSL-bzw. TLS-Verschlüsselung. Eine verschlüsselte Verbindung
              erkennen Sie daran, dass die Adresszeile des Browsers von
              "http://" auf "https://" wechselt und an dem Schloss-Symbol in
              Ihrer Browserzeile.
            </p>
            <p>
              Wenn die SSL- bzw. TLS-Verschlüsselung aktiviert ist, können die
              Daten, die Sie an uns übermitteln, nicht von Dritten mitgelesen
              werden.
            </p>
          </section>
          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">
              3. Datenerfassung auf unserer Website
            </h2>
            <h3 className="text-xl font-semibold mb-2">Cookies</h3>
            <p className="mb-4">
              Die Internetseiten verwenden teilweise so genannte Cookies.
              Cookies richten auf Ihrem Rechner keinen Schaden an und enthalten
              keine Viren. Cookies dienen dazu, unser Angebot nutzerfreundlicher,
              effektiver und sicherer zu machen. Cookies sind kleine
              Textdateien, die auf Ihrem Rechner abgelegt werden und die Ihr
              Browser speichert.
            </p>
            <p className="mb-4">
              Die meisten der von uns verwendeten Cookies sind so genannte
              "Session-Cookies". Sie werden nach Ende Ihres Besuchs automatisch
              gelöscht. Andere Cookies bleiben auf Ihrem Endgerät gespeichert
              bis Sie diese löschen. Diese Cookies ermöglichen es uns, Ihren
              Browser beim nächsten Besuch wiederzuerkennen.
            </p>
            <p className="mb-4">
              Sie können Ihren Browser so einstellen, dass Sie über das Setzen
              von Cookies informiert werden und Cookies nur im Einzelfall
              erlauben, die Annahme von Cookies für bestimmte Fälle oder
              generell ausschließen sowie das automatische Löschen der Cookies
              beim Schließen des Browser aktivieren. Bei der Deaktivierung von
              Cookies kann die Funktionalität dieser Website eingeschränkt sein.
            </p>
            <p>
              Cookies, die zur Durchführung des elektronischen
              Kommunikationsvorgangs oder zur Bereitstellung bestimmter, von
              Ihnen erwünschter Funktionen (z.B. Warenkorbfunktion) erforderlich
              sind, werden auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO
              gespeichert. Der Websitebetreiber hat ein berechtigtes Interesse
              an der Speicherung von Cookies zur technisch fehlerfreien und
              optimierten Bereitstellung seiner Dienste. Soweit andere Cookies
              (z.B. Cookies zur Analyse Ihres Surfverhaltens) gespeichert
              werden, werden diese in dieser Datenschutzerklärung gesondert
              behandelt.
            </p>
          </section>
          <section className="mb-8">
            <h3 className="text-xl font-semibold mb-2">Server-Log-Dateien</h3>
            <p className="mb-4">
              Der Provider der Seiten erhebt und speichert automatisch
              Informationen in so genannten Server-Log-Dateien, die Ihr Browser
              automatisch an uns übermittelt. Dies sind:
            </p>
            <ul className="list-disc list-inside mb-4">
              <li>Browsertyp und Browserversion</li>
              <li>verwendetes Betriebssystem</li>
              <li>Referrer URL</li>
              <li>Hostname des zugreifenden Rechners</li>
              <li>Uhrzeit der Serveranfrage</li>
              <li>IP-Adresse</li>
            </ul>
            <p className="mb-4">
              Eine Zusammenführung dieser Daten mit anderen Datenquellen wird
              nicht vorgenommen.
            </p>
            <p>
              Grundlage für die Datenverarbeitung ist Art. 6 Abs. 1 lit. f
              DSGVO, der die Verarbeitung von Daten zur Erfüllung eines Vertrags
              oder vorvertraglicher Maßnahmen gestattet.
            </p>
          </section>
          <section className="mb-8">
            <h3 className="text-xl font-semibold mb-2">Kontaktformular</h3>
            <p className="mb-4">
              Wenn Sie uns per Kontaktformular Anfragen zukommen lassen, werden
              Ihre Angaben aus dem Anfrageformular inklusive der von Ihnen dort
              angegebenen Kontaktdaten zwecks Bearbeitung der Anfrage und für
              den Fall von Anschlussfragen bei uns gespeichert. Diese Daten
              geben wir nicht ohne Ihre Einwilligung weiter.
            </p>
            <p className="mb-4">
              Die Verarbeitung der in das Kontaktformular eingegebenen Daten
              erfolgt somit ausschließlich auf Grundlage Ihrer Einwilligung
              (Art. 6 Abs. 1 lit. a DSGVO). Sie können diese Einwilligung
              jederzeit widerrufen. Dazu reicht eine formlose Mitteilung per
              E-Mail an uns. Die Rechtmäßigkeit der bis zum Widerruf erfolgten
              Datenverarbeitungsvorgänge bleibt vom Widerruf unberührt.
            </p>
            <p>
              Die von Ihnen im Kontaktformular eingegebenen Daten verbleiben bei
              uns, bis Sie uns zur Löschung auffordern, Ihre Einwilligung zur
              Speicherung widerrufen oder der Zweck für die Datenspeicherung
              entfällt (z.B. nach abgeschlossener Bearbeitung Ihrer Anfrage).
              Zwingende gesetzliche Bestimmungen – insbesondere
              Aufbewahrungsfristen – bleiben unberührt.
            </p>
          </section>
          <section>
            <h2 className="text-2xl font-bold mb-4">4. Plugins und Tools</h2>
            <h3 className="text-xl font-semibold mb-2">Google Web Fonts</h3>
            <p className="mb-4">
              Diese Seite nutzt zur einheitlichen Darstellung von Schriftarten
              so genannte Web Fonts, die von Google bereitgestellt werden. Beim
              Aufruf einer Seite lädt Ihr Browser die benötigten Web Fonts in
              ihren Browsercache, um Texte und Schriftarten korrekt anzuzeigen.
            </p>
            <p className="mb-4">
              Zu diesem Zweck muss der von Ihnen verwendete Browser Verbindung
              zu den Servern von Google aufnehmen. Hierdurch erlangt Google
              Kenntnis darüber, dass über Ihre IP-Adresse unsere Website
              aufgerufen wurde. Die Nutzung von Google Web Fonts erfolgt im
              Interesse einer einheitlichen und ansprechenden Darstellung
              unserer Online-Angebote. Dies stellt ein berechtigtes Interesse im
              Sinne von Art. 6 Abs. 1 lit. f DSGVO dar.
            </p>
            <p className="mb-4">
              Wenn Ihr Browser Web Fonts nicht unterstützt, wird eine
              Standardschrift von Ihrem Computer genutzt.
            </p>
            <p>
              Weitere Informationen zu Google Web Fonts finden Sie unter{" "}
              <a
                href="https://developers.google.com/fonts/faq"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                https://developers.google.com/fonts/faq
              </a>{" "}
              und in der Datenschutzerklärung von Google:{" "}
              <a
                href="https://www.google.com/policies/privacy/"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                https://www.google.com/policies/privacy/
              </a>
              .
            </p>
          </section>
        </section>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
// Main Application Content
// --------------------------------------------------------------------------
function MainContent() {
  return (
    <div className="relative font-sans bg-[#060606] text-gray-100">
      {/* Fullscreen Scroll Container */}
      <div
        className="h-screen overflow-y-scroll scroll-container"
        style={{ scrollBehavior: "smooth" }}
      >
        <Banner />
        <CategoriesSection />
        {/* This is where our Scene with the Cylinder is rendered */}
        <Scene />
        {/* <CylindricalCarousel /> */}
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
// App with Routing
// --------------------------------------------------------------------------
export default function App() {

  return (
    <BrowserRouter>
      <CookieConsentPopup />
    
        <Routes>
          <Route
            path="/"
            element={
              <Layout>
                <MainContent />
              </Layout>
            }
          />
          <Route
            path="/datenschutz"
            element={
              <Layout>
                <PrivacyPolicyPage />
              </Layout>
            }
          />
          <Route
            path="/impressum"
            element={
              <Layout>
                <Impressum />
              </Layout>
            }
          />
          <Route
            path="/aboutus"
            element={
              <Layout>
                <AboutUs />
              </Layout>
            }
          />
          <Route
            path="/contact"
            element={
              <Layout>
                <ContactUs />
              </Layout>
            }
          />
          <Route
            path="/Haftungsausschluss"
            element={
              <Layout>
                <Haftungsausschluss />
              </Layout>
            }
          />
        </Routes>
   
    </BrowserRouter>
  );
}
